import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Popover,
  MenuItem,
  Chip,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import CloseIcon from "@mui/icons-material/Close";
import { CATEGORIES_OPTIONS, TAGS_OPTIONS } from "../../Strategies/constants";

// ─── Filter Dropdown ──────────────────────────────────────────────────────────
const FilterDropdown = ({ label, options, selected, onToggle }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // options can be string[] or { value, label }[]
  const normalize = (opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt;

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "none",
              fontSize: "1rem !important",
            }}
          />
        }
        sx={{
          border: `1px solid ${open ? theme.palette.primary.main : theme.palette.divider}`,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.default,
          textTransform: "none",
          px: 2,
          py: 0.875,
          fontSize: "0.875rem",
          borderRadius: 1,
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        {label}
        {selected.length > 0 && (
          <Box
            component="span"
            sx={{
              ml: 1,
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              border: `1px solid ${theme.palette.primary.main}`,
              color: theme.palette.primary.main,
              fontSize: "0.7rem",
              lineHeight: 1.2,
            }}
          >
            {selected.length}
          </Box>
        )}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            maxHeight: 260,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          },
        }}
      >
        {options.map((opt) => {
          const { value, label: optLabel } = normalize(opt);
          const isSelected = selected.includes(value);
          return (
            <MenuItem
              key={value}
              onClick={() => onToggle(value)}
              sx={{
                color: isSelected ? "primary.main" : "text.secondary",
                fontSize: "0.85rem",
                py: 1.25,
                display: "flex",
                justifyContent: "space-between",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              {optLabel}
              {isSelected && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 0.5,
                    backgroundColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    ml: 1,
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Box>
              )}
            </MenuItem>
          );
        })}
      </Popover>
    </>
  );
};

// ─── Store Filters ────────────────────────────────────────────────────────────
const StoreFilters = ({ searchTerm, onSearchChange, filters, onChange, availableOwners }) => {
  const theme = useTheme();
  const [showFilters, setShowFilters] = useState(true);

  const hasAnyActive =
    !!searchTerm ||
    filters.tags.length > 0 ||
    filters.categories.length > 0 ||
    filters.owners.length > 0;

  const toggleFilter = (key, value) => {
    onChange({
      ...filters,
      [key]: filters[key].includes(value)
        ? filters[key].filter((v) => v !== value)
        : [...filters[key], value],
    });
  };

  const handleSortByToggle = () => {
    const nextSortBy = filters.sortBy === "name" ? "return" : "name";
    const nextDir = filters.sortDir || "asc";
    onChange({ ...filters, sortBy: nextSortBy, sortDir: nextDir });
  };

  const handleSortDirToggle = () => {
    onChange({ ...filters, sortDir: filters.sortDir === "asc" ? "desc" : "asc" });
  };

  const clearAll = () => {
    onSearchChange("");
    onChange({ tags: [], categories: [], owners: [], sortBy: "", sortDir: "" });
  };

  const sortLabel = filters.sortBy === "name" ? "Name" : "Return";

  return (
    <Box
      sx={{
        mb: 3,
        p: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      {/* Row 1: Search + Filters toggle */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search strategies..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.secondary", fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme.palette.background.default,
              "& fieldset": { borderColor: theme.palette.divider },
              "&:hover fieldset": { borderColor: theme.palette.primary.main },
              "&.Mui-focused fieldset": { borderColor: theme.palette.primary.main },
            },
          }}
        />

        <Button
          onClick={() => setShowFilters(!showFilters)}
          startIcon={<TuneIcon sx={{ fontSize: "1rem !important" }} />}
          endIcon={
            <KeyboardArrowDownIcon
              sx={{
                transition: "transform 0.2s",
                transform: showFilters ? "rotate(180deg)" : "none",
                fontSize: "1rem !important",
              }}
            />
          }
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            backgroundColor: "transparent",
            textTransform: "none",
            whiteSpace: "nowrap",
            px: 2,
            borderRadius: 1,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          Filters
        </Button>
      </Box>

      {/* Row 2: Dropdowns + sort (collapsible) */}
      {showFilters && (
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
            <FilterDropdown
              label="Categories"
              options={CATEGORIES_OPTIONS}
              selected={filters.categories}
              onToggle={(val) => toggleFilter("categories", val)}
            />

            <FilterDropdown
              label="Tags"
              options={TAGS_OPTIONS}
              selected={filters.tags}
              onToggle={(val) => toggleFilter("tags", val)}
            />

            {availableOwners.length > 0 && (
              <FilterDropdown
                label="Owners"
                options={availableOwners.map((o) => ({ value: o.id, label: o.name }))}
                selected={filters.owners}
                onToggle={(val) => toggleFilter("owners", val)}
              />
            )}

            {/* Sort control */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.875,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                backgroundColor: theme.palette.background.default,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Sort by:
              </Typography>
              <Box
                component="button"
                onClick={handleSortByToggle}
                sx={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "primary.main",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  p: 0,
                  "&:hover": { opacity: 0.8 },
                }}
              >
                {sortLabel}
                <SwapVertIcon sx={{ fontSize: "0.875rem" }} />
              </Box>

              {filters.sortBy && (
                <Box
                  component="button"
                  onClick={handleSortDirToggle}
                  sx={{
                    background: "rgba(6, 182, 212, 0.1)",
                    border: "1px solid rgba(6, 182, 212, 0.5)",
                    borderRadius: 0.75,
                    cursor: "pointer",
                    color: "white",
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    "&:hover": { background: "rgba(6, 182, 212, 0.2)" },
                  }}
                >
                  {filters.sortDir === "asc" ? (
                    <TrendingUpIcon sx={{ fontSize: "0.875rem" }} />
                  ) : (
                    <TrendingDownIcon sx={{ fontSize: "0.875rem" }} />
                  )}
                  {filters.sortDir === "asc" ? "Asc" : "Desc"}
                </Box>
              )}
            </Box>

            {/* Clear all */}
            {hasAnyActive && (
              <Box
                component="button"
                onClick={clearAll}
                sx={{
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.5)",
                  borderRadius: 1,
                  cursor: "pointer",
                  color: "#ef4444",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.875,
                  "&:hover": { background: "rgba(239, 68, 68, 0.2)" },
                }}
              >
                <CloseIcon sx={{ fontSize: "1rem" }} />
                Clear filters
              </Box>
            )}
          </Box>

          {/* Active filter chips */}
          {hasAnyActive && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
              {searchTerm && (
                <Chip
                  label={`Search: "${searchTerm}"`}
                  size="small"
                  onDelete={() => onSearchChange("")}
                  sx={{
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    border: "1px solid rgba(59, 130, 246, 0.5)",
                    color: "#60a5fa",
                    "& .MuiChip-deleteIcon": { color: "#60a5fa" },
                  }}
                />
              )}
              {filters.categories.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  size="small"
                  onDelete={() => toggleFilter("categories", cat)}
                  sx={{
                    backgroundColor: "rgba(6, 182, 212, 0.1)",
                    border: "1px solid rgba(6, 182, 212, 0.5)",
                    color: "#06b6d4",
                    "& .MuiChip-deleteIcon": { color: "#06b6d4" },
                  }}
                />
              ))}
              {filters.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => toggleFilter("tags", tag)}
                  sx={{
                    backgroundColor: "rgba(168, 85, 247, 0.1)",
                    border: "1px solid rgba(168, 85, 247, 0.5)",
                    color: "#a855f7",
                    "& .MuiChip-deleteIcon": { color: "#a855f7" },
                  }}
                />
              ))}
              {filters.owners.map((ownerId) => {
                const owner = availableOwners.find((o) => o.id === ownerId);
                return (
                  <Chip
                    key={ownerId}
                    label={owner?.name || ownerId}
                    size="small"
                    onDelete={() => toggleFilter("owners", ownerId)}
                    sx={{
                      backgroundColor: "rgba(249, 115, 22, 0.1)",
                      border: "1px solid rgba(249, 115, 22, 0.5)",
                      color: "#f97316",
                      "& .MuiChip-deleteIcon": { color: "#f97316" },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StoreFilters;

